import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './hooks/useAuth';
import Login from './pages/Login';
import LoginPin from './pages/LoginPin';
import MagicLinkConsumer from './pages/MagicLinkConsumer';
import Hub from './pages/Hub';
import Shoots from './pages/Shoots';
import Gallery from './pages/Gallery';
import Detail from './pages/Detail';
import Compare from './pages/Compare';
import SetView from './pages/SetView';
import Search from './pages/Search';
import Portfolios from './pages/Portfolios';
import LandingHub from './landing/LandingHub';
import LandingModels from './landing/LandingModels';
import LandingPhotographers from './landing/LandingPhotographers';
import LandingDuos from './landing/LandingDuos';
import { ScrollToTop } from './landing/ScrollToTop';
import PortfolioViewer from './pages/PortfolioViewer';

// Wrappar privata routes — väntar in `useAuth` och redirectar till /login om
// användaren inte är inloggad. `from` skickas via state så vi kan redirecta
// tillbaka efter lyckad login.
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-loupe-text-3 text-sm tracking-[0.2em] uppercase animate-pulse">
          Loading…
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  return <>{children}</>;
}

// Vuelette är split i två domäner som båda serverar samma frontend-bundle:
//   vuelette.com / www.vuelette.com  = marketing-landing (LandingHub)
//   app.vuelette.com                 = produkten (RequireAuth → Hub, eller Login)
// Vi avgör vilket utifrån window.location.hostname. Detta gör att vi kan
// behålla en singel deploy men ge användarna en konsekvent split.
function isAppDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  // app.vuelette.com är produktdomänen. Allt annat (vuelette.com,
  // www.vuelette.com, loupe.hallengren.fr, localhost) behandlas som
  // marketing OM oinloggad. Loupe-legacy och localhost får ändå vara
  // produkten för bakåtkompat.
  if (host.startsWith('app.')) return true;
  // Fallback: localhost och loupe.hallengren.fr behåller produkt-only
  // beteende (ingen marketing-rendering) eftersom det är dev/legacy.
  if (host === 'localhost' || host === '127.0.0.1') return true;
  if (host === 'loupe.hallengren.fr') return true;
  return false;
}

// Root-route. Beteendet beror på domänen + auth-status:
//   - app.vuelette.com inloggad     → Hub
//   - app.vuelette.com oinloggad    → Navigate till /login
//   - vuelette.com inloggad         → Hub (kort flicker, sen redirect via banner)
//   - vuelette.com oinloggad        → LandingHub (marketing)
function RootGate() {
  const { user, isLoading } = useAuth();
  const onApp = isAppDomain();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-loupe-text-3 text-sm tracking-[0.2em] uppercase animate-pulse">
          Loading…
        </div>
      </div>
    );
  }

  if (onApp) {
    // Produkt-domän — kräver auth.
    return user ? <Hub /> : <Navigate to="/login" replace />;
  }

  // Marketing-domän — visa landing oavsett om man är inloggad
  // (inloggade kan klicka Sign in för att gå till app.vuelette.com).
  return <LandingHub />;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/login/pin" element={<LoginPin />} />
      <Route path="/auth/m/:token" element={<MagicLinkConsumer />} />
      <Route path="/auth/m" element={<MagicLinkConsumer />} />

      {/* Portfolio public viewer — alltid public, oavsett auth eller domain */}
      <Route path="/p/:slug" element={<PortfolioViewer />} />

      {/* Rot: villkorlig — landing för unauth, Hub för auth */}
      <Route path="/" element={<RootGate />} />

      {/* Publika landing-sidor (alltid synliga — inloggade ser dem också) */}
      <Route path="/for-models" element={<LandingModels />} />
      <Route path="/for-photographers" element={<LandingPhotographers />} />
      <Route path="/for-duos" element={<LandingDuos />} />

      {/* Bakåtkompatibilitet: /shoots visar gamla shoot-listan */}
      <Route
        path="/shoots"
        element={
          <RequireAuth>
            <Shoots />
          </RequireAuth>
        }
      />
      <Route
        path="/shoots/:shootId"
        element={
          <RequireAuth>
            <Gallery />
          </RequireAuth>
        }
      />
      <Route
        path="/shoots/:shootId/compare"
        element={
          <RequireAuth>
            <Compare />
          </RequireAuth>
        }
      />
      <Route
        path="/shoots/:shootId/images/:imageId"
        element={
          <RequireAuth>
            <Detail />
          </RequireAuth>
        }
      />

      {/* Sets */}
      <Route
        path="/sets/:setId"
        element={
          <RequireAuth>
            <SetView />
          </RequireAuth>
        }
      />
      {/* Detail inom set-kontext — prev/next och back navigerar inom setet */}
      <Route
        path="/sets/:setId/images/:imageId"
        element={
          <RequireAuth>
            <Detail />
          </RequireAuth>
        }
      />

      {/* Search */}
      <Route
        path="/search"
        element={
          <RequireAuth>
            <Search />
          </RequireAuth>
        }
      />

      {/* Portfolios */}
      <Route
        path="/portfolios"
        element={
          <RequireAuth>
            <Portfolios />
          </RequireAuth>
        }
      />

      {/* Catch-all → Hub */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
